import { useRef } from 'react'

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FormattedNumber, useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import type { OrchestratedListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import {
  AnalyticsEntityType,
  AnalyticsSource,
  FormattedMessage,
  Tooltip,
} from '@b-stock/fe-core/bstock-react'
import { Colors, pxToRem, Typography } from '@b-stock/fe-core/design-system'

import {
  useIsPforStorefrontExcluded,
  useProblemFreeOrderRateAnalytics,
} from '@/buyer-portal/components/features/listing/hooks/useProblemFreeOrderRateAnalytics'
import getListingAnalyticsData from '@/buyer-portal/helpers/getListingAnalyticsData'
import { FeatureFlags } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

type ScoreColor = 'green' | 'black'

const SCORE_COLORS: Record<ScoreColor, string> = {
  green: Colors.Semantic.Success.onhover,
  black: Colors.Semantic.Neutral.black,
}

// Value is a decimal between 0 and 1 (e.g. 0.95 = 95%)
const getScoreColor = (value: number): ScoreColor => {
  if (value >= 0.95) return 'green'
  return 'black'
}

const Container = styled.div`
  align-items: center;
  background: ${Colors.Semantic.Neutral.white};
  border: ${pxToRem(1)} solid ${Colors.Semantic.Neutral.medium_grey};
  border-radius: 0.5rem;
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding: 0.5rem 1rem;
`

const LabelGroup = styled.div`
  align-items: center;
  display: flex;
  gap: 0.5rem;
`

const LabelText = styled.span`
  ${Typography.Title3}
  color: ${Colors.Semantic.Neutral.black};
`

const TooltipIcon = styled(FontAwesomeIcon)`
  color: ${Colors.Semantic.Neutral.black};
  cursor: pointer;
`

const ValueText = styled.span<{ $color: ScoreColor }>`
  ${Typography.Title2}
  color: ${({ $color }) => SCORE_COLORS[$color]};
`

const InsufficientDataText = styled.span`
  ${Typography.Subtitle1}
  color: ${Colors.Semantic.Info.highlight};
`

const ProblemFreeOrderRate = ({
  listing,
}: {
  listing: OrchestratedListingDetails
}) => {
  const tooltipIconRef = useRef<HTMLSpanElement>(null)
  const { formatMessage } = useIntl()
  const { trackButtonClicked } = useAnalytics()
  const config = useFlag(FeatureFlags.ENABLE_LDP_PROBLEM_FREE_ORDER_RATE)
  const fmvBadgeFlag = useFlag(FeatureFlags.ENABLE_FMV_DEAL_BADGE)
  const personId = useDecodedAccessToken()?.personId
  const { facility_performance_rate, is_facility_performance_rate_shown } =
    useProblemFreeOrderRateAnalytics(listing)
  const isStorefrontExcluded = useIsPforStorefrontExcluded(listing)

  const handleTooltipShow = () => {
    trackButtonClicked({
      buttonName: 'facility_performance_rate_tooltip',
      screenName: 'listing_detail_page',
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      ...getListingAnalyticsData(listing, fmvBadgeFlag),
      facility_performance_rate,
      is_facility_performance_rate_shown,
    })
  }

  const value =
    (listing as { problemFreeOrderRate?: number | null })
      .problemFreeOrderRate ?? null
  const variant = config.variant || 'c'

  if (!config.enabled || !personId || isStorefrontExcluded) return null

  return (
    <Container data-testid="problem-free-order-rate">
      <LabelGroup>
        <LabelText data-testid="problem-free-order-rate-label">
          <FormattedMessage id={`Filters.problemFreeOrderRate.${variant}`} />
        </LabelText>
        <span
          ref={tooltipIconRef}
          role="img"
          tabIndex={0}
          aria-label={formatMessage({
            id: 'Filters.problemFreeOrderRate.tooltipAriaLabel',
          })}
        >
          <TooltipIcon icon={faCircleInfo} />
        </span>
        <Tooltip
          // oxlint-disable-next-line react/refs
          referenceElement={tooltipIconRef.current}
          onShow={handleTooltipShow}
        >
          <FormattedMessage id="Filters.problemFreeOrderRate.tooltip" />
        </Tooltip>
      </LabelGroup>
      {value !== null ? (
        <ValueText
          $color={getScoreColor(value)}
          data-testid="problem-free-order-rate-value"
        >
          <FormattedNumber
            value={value}
            style="percent"
            maximumFractionDigits={0}
          />
        </ValueText>
      ) : (
        <InsufficientDataText data-testid="problem-free-order-rate-value">
          <FormattedMessage id="Listing.listingDetails.problemFreeOrderRate.insufficientData" />
        </InsufficientDataText>
      )}
    </Container>
  )
}

export default ProblemFreeOrderRate
